/**
 * kXmlTextCore - XML text helpers with no DOM and no dependencies.
 *
 * Lifted 2026-10-09 (reuse review H6) from kAzureBlobCore.XmlUnescaped and ReUsableTS
 * kOfficeTextReplacer.UnescapeXml. Owner decision 2026-10-10: the first step is `String()`, NOT `nz()` -
 * nz() turns the literal STRINGS "null" and "undefined" into "", which would blank real text.
 *
 * ⚠ ONLY THE FIVE XML ENTITIES. Numeric (`&#39;`) and HTML-named (`&nbsp;`) entities are left as
 * they are - use kHtmlParserCore.DecodeEntities for HTML text, not this.
 */
export class kXmlTextCore {
    /** Strip through the first namespace colon; optionally lowercase the local part.
     * DOCUMENTED EXCEPTION: pure strict string operations; callers own gates, coercion and errors. */
    public static LocalName = (QName: string, Lower: boolean = false): string => {
        const Colon: number = QName.indexOf(":");
        let Local: string = QName;
        if (Colon !== -1) { Local = QName.substring(Colon + 1); }
        if (Lower === true) { return Local.toLowerCase(); }
        return Local;
    };
    /** Five XML attribute entities by default, with the existing SharePoint &apos; spelling.
     * EscapeApostrophe=false retains four HTML attribute entities without the HTML4-unknown &apos;.
     * DOCUMENTED EXCEPTION: pure strict string replacement; callers own gates, coercion and errors. */
    public static EscapeAttribute = (Text: string, EscapeApostrophe: boolean = true): string => {
        const Out: string = Text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
        if (EscapeApostrophe === true) { return Out.replace(/'/g, "&apos;"); }
        return Out;
    };

    /** XML element text: ampersand first, then angle brackets; quotes need no escaping in text nodes. Default String() coercion is unchanged; Coerce=false retains native strict string faults.
     * DOCUMENTED EXCEPTION: pure replacement, callers own their gates and input normalisation. */
    public static Escape = (Text: string, Coerce: boolean = true): string => {
        let Value: string = Text;
        if (Coerce === true) { Value = String(Text); }
        return Value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    };

    /**
     * `&lt; &gt; &quot; &apos;` then `&amp;` LAST, so `&amp;lt;` stays the literal text `&lt;`.
     *
     * DOCUMENTED EXCEPTION: pure string replacement; no I/O, no state, cannot fail on a string.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, no gate - its callers keep their own gates.
     */
    public static Unescape = (Text: string): string => {
        return String(Text)
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, "\"")
            .replace(/&apos;/g, "'")
            .replace(/&amp;/g, "&");
    };
}

